/**
 * Single source of truth for SEO metadata, consumed by TWO runtimes:
 *
 *   1. The browser  — src/lib/seo.ts `useSeo()` reads these per route and keeps
 *      <head> correct on client-side (SPA) navigation + EN/IT language toggle.
 *   2. The build    — scripts/prerender.mjs reads these and bakes the per-route
 *      <head> into dist/<route>/index.html so social scrapers (which never run
 *      JS) and crawlers get correct static metadata.
 *
 * It is a plain `.mjs` (not `.ts`) precisely so the Node build script can import
 * it directly without a compile step; the browser side is typed via the
 * companion `seo-data.d.mts`. Keeping it in ONE file means the two runtimes can
 * never drift apart. Pure data + pure functions only — no React, no Node APIs.
 */

const DOMAIN = 'https://rivaimmobiliaresrl.com';
const OG_IMAGE_PATH = '/og/charter-1200x630.jpg';
const ORG_ID = `${DOMAIN}/#org`;

export const SITE = {
  domain: DOMAIN,
  brand: 'Virtus',
  legalName: 'Riva Immobiliare S.r.l.',
  defaultLang: 'en',
  ogImagePath: OG_IMAGE_PATH,
  contactEmail: 'giorgio.l@virtussrl.it',
  twitterCard: 'summary_large_image',
};

/**
 * The locale registry — the one place a language is defined. Node (prerender, sitemap)
 * and the browser (router, useSeo, links) both fan out over it, so a locale added here
 * reaches every surface at once. Exactly ONE locale has `prefix: ''`: the default, whose
 * URLs are the historical unprefixed ones (nothing already indexed moves).
 */
export const LOCALES = {
  en: { prefix: '', htmlLang: 'en', ogLocale: 'en_GB', hreflang: 'en' },
  it: { prefix: '/it', htmlLang: 'it', ogLocale: 'it_IT', hreflang: 'it' },
};

export const DEFAULT_LANG = 'en';

/** Every language, in registry order (the default first). */
export const LANGS = Object.keys(LOCALES);

/** Drop a trailing slash from any path but the root. */
function trimSlash(path) {
  return path.length > 1 && path.endsWith('/') ? path.slice(0, -1) : path;
}

/**
 * The URL path of a locale-neutral route path in `lang`:
 * ('/', 'it') → '/it' · ('/yacht', 'it') → '/it/yacht' · (p, 'en') → p.
 */
export function localePath(path, lang) {
  const base = path || '/';
  const { prefix } = LOCALES[lang];
  if (!prefix) return base;
  return base === '/' ? prefix : `${prefix}${base}`;
}

/**
 * The inverse of `localePath`: which locale a URL path is in, and the locale-neutral
 * route path behind it (trailing slash normalised). The prefix matches a whole segment
 * — '/italy' is English, '/it' and '/it/' are the Italian root.
 */
export function splitLocale(pathname) {
  const path = pathname || '/';
  for (const lang of LANGS) {
    const { prefix } = LOCALES[lang];
    if (!prefix) continue;
    if (path === prefix || path === `${prefix}/`) return { lang, path: '/' };
    if (path.startsWith(`${prefix}/`)) return { lang, path: trimSlash(path.slice(prefix.length)) };
  }
  return { lang: DEFAULT_LANG, path: trimSlash(path) };
}

/** Absolute canonical URL for a route path in `lang` (default: the default locale).
 *  The site root keeps its trailing slash; every other URL — the IT home `/it`
 *  included — is the clean, no-trailing-slash form that the in-app links use. */
export function absoluteUrl(path, lang = DEFAULT_LANG) {
  const localized = localePath(path, lang);
  if (localized === '/') return `${DOMAIN}/`;
  return `${DOMAIN}${localized}`;
}

/** The robots directive of a page head — shared by the prerender and useSeo. */
export function robotsFor(page) {
  return page.noindex ? 'noindex,follow' : 'index,follow,max-image-preview:large,max-snippet:-1';
}

/**
 * The hreflang alternates of a route: one per locale plus `x-default` → the default
 * locale's URL. Identical for every language version of the page (reciprocity by
 * construction). A non-indexable route has none — a noindex page gains nothing.
 */
export function alternates(route) {
  if (!route.indexable) return [];
  return [
    ...LANGS.map((lang) => ({ hreflang: LOCALES[lang].hreflang, href: absoluteUrl(route.path, lang) })),
    { hreflang: 'x-default', href: absoluteUrl(route.path, DEFAULT_LANG) },
  ];
}

export function ogImageUrl() {
  return `${DOMAIN}${OG_IMAGE_PATH}`;
}

function breadcrumb(items, lang) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((it, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: it.name[lang],
      item: absoluteUrl(it.path, lang),
    })),
  };
}

const HOME_CRUMB = { name: { en: 'Home', it: 'Home' }, path: '/' };

/** The charter offer — reused by the yacht Product and the availability Service.
 *  "From €3,800/day": the low-season starting day-rate (the entry tier of the
 *  three seasonal rates shown on /availability), no fabricated stock/ratings. */
function charterOffer(lang) {
  return {
    '@type': 'Offer',
    priceCurrency: 'EUR',
    price: '3800',
    priceSpecification: {
      '@type': 'UnitPriceSpecification',
      price: '3800',
      priceCurrency: 'EUR',
      unitText: 'day',
      referenceQuantity: { '@type': 'QuantitativeValue', value: 1, unitText: 'day' },
    },
    url: absoluteUrl('/availability', lang),
    seller: { '@id': ORG_ID },
    areaServed: 'Costa Smeralda · La Maddalena, Sardinia',
  };
}

export const ROUTES = [
  {
    path: '/',
    indexable: true,
    changefreq: 'weekly',
    priority: '1.0',
    title: {
      en: 'Pershing 58 Yacht Charter, Porto Cervo | Virtus',
      it: 'Noleggio Pershing 58 a Porto Cervo | Virtus',
    },
    description: {
      en: 'Charter the Pershing 58 in Porto Cervo: a 17m Italian motor yacht for day cruises, sunsets and weekly itineraries across La Maddalena. From €3,800/day.',
      it: 'Noleggia il Pershing 58 a Porto Cervo: yacht italiano di 17m per crociere giornaliere, tramonti e itinerari settimanali alla Maddalena. Da €3.800/giorno.',
    },
    jsonLd: (lang) => [
      {
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: 'Pershing 58 — Private Yacht Charter',
        image: ogImageUrl(),
        description: ROUTES[0].description[lang],
        brand: { '@type': 'Brand', name: 'Pershing' },
        category: 'Yacht charter',
        offers: charterOffer(lang),
      },
    ],
    lcpPreload: { href: '/img/hero-loop-poster.jpg' },
  },
  {
    path: '/yacht',
    indexable: true,
    changefreq: 'monthly',
    priority: '0.8',
    title: {
      en: 'Pershing 58 — Specs & Interior | Virtus',
      it: 'Pershing 58 — Specifiche e Interni | Virtus',
    },
    description: {
      en: "Pershing 58 specs: 17.7m planing hardtop, twin MTU surface drives, 42 knots, Kevlar hull. Owner's suite, helm and sundeck — captain and crew included.",
      it: 'Pershing 58: 17,7m hardtop planante, doppi MTU con eliche di superficie, 42 nodi, scafo Kevlar. Cabina armatoriale e plancia, equipaggio incluso.',
    },
    jsonLd: (lang) => [
      breadcrumb([HOME_CRUMB, { name: { en: 'The Yacht', it: 'Lo Yacht' }, path: '/yacht' }], lang),
      {
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: 'Pershing 58',
        image: ogImageUrl(),
        description: ROUTES[1].description[lang],
        brand: { '@type': 'Brand', name: 'Pershing' },
        category: 'Motor yacht',
        additionalProperty: [
          { '@type': 'PropertyValue', name: 'Length overall', value: '17.7 m' },
          { '@type': 'PropertyValue', name: 'Beam', value: '4.30 m' },
          { '@type': 'PropertyValue', name: 'Top speed', value: '42 knots' },
          { '@type': 'PropertyValue', name: 'Cruising speed', value: '36 knots' },
          { '@type': 'PropertyValue', name: 'Engines', value: '2 × MTU surface drives' },
          { '@type': 'PropertyValue', name: 'Guests', value: '12 day' },
          { '@type': 'PropertyValue', name: 'Crew', value: 'MCA Master 200 captain + stewardess' },
        ],
        offers: charterOffer(lang),
      },
    ],
    lcpPreload: { href: '/img/yacht-hero-poster.jpg' },
  },
  {
    path: '/location',
    indexable: true,
    changefreq: 'monthly',
    priority: '0.7',
    title: {
      en: 'Porto Cervo & La Maddalena Cruising | Virtus',
      it: 'Crociere a Porto Cervo e La Maddalena | Virtus',
    },
    description: {
      en: "Porto Cervo is home to the Pershing 58 — a protected Costa Smeralda berth, 30 minutes from La Maddalena's five islands, turquoise coves and Maestrale winds.",
      it: 'Porto Cervo è la base del Pershing 58: ormeggio protetto in Costa Smeralda, a 30 minuti dalle cinque isole della Maddalena, cale turchesi e Maestrale.',
    },
    jsonLd: (lang) => [
      breadcrumb([HOME_CRUMB, { name: { en: 'Porto Cervo', it: 'Porto Cervo' }, path: '/location' }], lang),
      {
        '@context': 'https://schema.org',
        '@type': 'TouristAttraction',
        name: 'Porto Cervo & the La Maddalena Archipelago',
        description: ROUTES[2].description[lang],
        image: ogImageUrl(),
        geo: { '@type': 'GeoCoordinates', latitude: 41.133, longitude: 9.533 },
        address: {
          '@type': 'PostalAddress',
          addressLocality: 'Porto Cervo',
          addressRegion: 'Sardinia',
          addressCountry: 'IT',
        },
        containsPlace: [
          { '@type': 'Place', name: 'La Maddalena' },
          { '@type': 'Place', name: 'Caprera' },
          { '@type': 'Place', name: 'Spargi' },
          { '@type': 'Place', name: 'Budelli' },
          { '@type': 'Place', name: 'Razzoli' },
        ],
      },
    ],
  },
  {
    path: '/availability',
    indexable: true,
    changefreq: 'weekly',
    priority: '0.9',
    title: {
      en: 'Book the Pershing 58 · Availability | Virtus',
      it: 'Prenota il Pershing 58 · Disponibilità | Virtus',
    },
    description: {
      en: 'Check Pershing 58 availability at Porto Cervo. Half-day, full-day, weekend or week-long charters — send your preferred dates and group size.',
      it: 'Verifica la disponibilità del Pershing 58 a Porto Cervo. Charter di mezza giornata, giornata intera, weekend o settimana: invia date e ospiti.',
    },
    jsonLd: (lang) => [
      breadcrumb([HOME_CRUMB, { name: { en: 'Availability', it: 'Disponibilità' }, path: '/availability' }], lang),
      {
        '@context': 'https://schema.org',
        '@type': 'Service',
        serviceType: 'Private yacht charter',
        name: 'Pershing 58 Charter — Availability & Booking',
        description: ROUTES[3].description[lang],
        provider: { '@id': ORG_ID },
        areaServed: 'Costa Smeralda · La Maddalena, Sardinia',
        offers: charterOffer(lang),
        availableChannel: {
          '@type': 'ServiceChannel',
          serviceUrl: absoluteUrl('/availability', lang),
          availableLanguage: ['en', 'it'],
        },
      },
    ],
    lcpPreload: { href: '/img/p13-1600.jpg' },
  },
  {
    path: '/privacy',
    indexable: false,
    noindex: true,
    title: {
      en: 'Privacy Policy | Virtus',
      it: 'Informativa sulla privacy | Virtus',
    },
    description: {
      en: 'How Virtus handles personal data submitted through the Pershing 58 charter inquiry on this site.',
      it: 'Come Virtus tratta i dati personali inviati tramite la richiesta di charter del Pershing 58 su questo sito.',
    },
    jsonLd: () => [],
  },
];

/**
 * The not-found page's head, per language. Never a route: it is not in ROUTES, never
 * in the sitemap, and its block carries only a title, a description and `noindex` —
 * no canonical, no og:url, no hreflang, no page JSON-LD (a 404 must not claim a URL).
 * Prerendered to dist/404.html and dist/it/404.html, which nginx serves with status 404.
 */
export const NOT_FOUND = {
  path: null,
  notFound: true,
  indexable: false,
  noindex: true,
  title: {
    en: 'Page not found | Virtus',
    it: 'Pagina non trovata | Virtus',
  },
  description: {
    en: 'This address does not exist on the Virtus site. Return to the Pershing 58 charter home page.',
    it: 'Questo indirizzo non esiste sul sito Virtus. Torna alla home del charter del Pershing 58.',
  },
  jsonLd: () => [],
};

/** Site-wide JSON-LD baked statically into index.html (inherited by every
 *  prerendered page). Public brand = Virtus; legal owner recorded as legalName. */
export function siteJsonLd() {
  return [
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      '@id': ORG_ID,
      name: SITE.brand,
      legalName: SITE.legalName,
      vatID: 'IT13256780969',
      url: `${DOMAIN}/`,
      logo: `${DOMAIN}/logo-512-transparent.png`,
      image: ogImageUrl(),
      description:
        'Private charter of the Pershing 58, a 17-metre Italian motor yacht based at Marina di Poltu Quatu, Costa Smeralda.',
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Via Antonio Locatelli 14',
        postalCode: '20851',
        addressLocality: 'Lissone',
        addressRegion: 'Monza e Brianza',
        addressCountry: 'IT',
      },
      areaServed: 'Costa Smeralda, Sardinia',
      contactPoint: {
        '@type': 'ContactPoint',
        email: SITE.contactEmail,
        contactType: 'reservations',
        availableLanguage: ['en', 'it'],
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      '@id': `${DOMAIN}/#website`,
      url: `${DOMAIN}/`,
      name: 'Virtus — Pershing 58 Charter',
      inLanguage: ['en', 'it'],
      publisher: { '@id': ORG_ID },
    },
  ];
}
